/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useEffect, useRef, useState } from 'react';

import Link from 'next/link';
import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningCoverage.module.scss';

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path
      d="M2.6 8h10M8.4 3.8 12.6 8l-4.2 4.2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RegistryIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <rect
      x="6"
      y="5"
      width="16"
      height="19"
      rx="1.4"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <line
      x1="10"
      y1="11"
      x2="18"
      y2="11"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '90ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="10"
      y1="15.5"
      x2="18"
      y2="15.5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '160ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="10"
      y1="20"
      x2="15"
      y2="20"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '230ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const FinancialIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <line
      x1="4"
      y1="24"
      x2="24"
      y2="24"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <rect
      x="6"
      y="16"
      width="4"
      height="8"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '80ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="12"
      y="10"
      width="4"
      height="14"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '160ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="18"
      y="4"
      width="4"
      height="20"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '240ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const GroupIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle
      cx="14"
      cy="5.5"
      r="2.6"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M14 8.1v5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '70ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M6 21.4v-5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '140ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle
      cx="6"
      cy="23.4"
      r="2.4"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '220ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle
      cx="14"
      cy="23.4"
      r="2.4"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '260ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle
      cx="22"
      cy="23.4"
      r="2.4"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '300ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const ShareholdersIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle
      cx="14"
      cy="14"
      r="9.5"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M14 14V4.5A9.5 9.5 0 0 1 22.7 18"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '120ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

const OfficersIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <rect
      x="4"
      y="6"
      width="20"
      height="16"
      rx="2"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle
      cx="11"
      cy="13"
      r="2.6"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '110ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M6.8 19.2c.7-2.2 2.3-3.3 4.2-3.3s3.5 1.1 4.2 3.3"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '180ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="17.5"
      y1="11.5"
      x2="21"
      y2="11.5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '240ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="17.5"
      y1="15.5"
      x2="21"
      y2="15.5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '280ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const FirmographicIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle
      cx="14"
      cy="14"
      r="9.5"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M4.5 14h19"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '90ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M14 4.5c2.6 2.4 4 5.9 4 9.5s-1.4 7.1-4 9.5c-2.6-2.4-4-5.9-4-9.5s1.4-7.1 4-9.5Z"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '170ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

type Category = {
  num: string;
  count: number;
  title: string;
  icon: ReactNode;
  items: string[];
};

const CATEGORIES: Category[] = [
  {
    num: '01',
    count: 17,
    title: 'Company registry data',
    icon: <RegistryIcon />,
    items: [
      'Global Database ID, registration number, VAT number',
      'Company legal name and trading name',
      'Year of founding, company status, legal form',
      'Country, region, city, ZIP, geo coordinates',
      'Registered address and office locations',
      'Industry classification codes',
    ],
  },
  {
    num: '02',
    count: 38,
    title: 'Financial data',
    icon: <FinancialIcon />,
    items: [
      'Turnover, gross profit, operating profit, EBITDA',
      'Pre-tax profit, profit after tax, retained profits',
      'Total assets, fixed and current assets, stock',
      'Cash, trade debtors, trade creditors, liabilities',
      'Net assets, shareholder funds, capital employed',
      'Latest accounts date filed and reporting period',
    ],
  },
  {
    num: '03',
    count: 24,
    title: 'Group structure',
    icon: <GroupIcon />,
    items: [
      'Immediate parent: ID, name, address, country',
      'Domestic ultimate: ID, name, address, country',
      'Global ultimate: ID, name, address, country',
      'ISO country codes at every tier',
      'Linkage IDs for graph traversal',
    ],
  },
  {
    num: '04',
    count: 13,
    title: 'Shareholders',
    icon: <ShareholdersIcon />,
    items: [
      'Shareholder name, type and country',
      'Ownership percentage and number of shares',
      'Share class and nominal value',
      'UBO flag and PSC nature of control',
      'Date notified and corporate shareholder ID',
    ],
  },
  {
    num: '05',
    count: 12,
    title: 'Officers and directors',
    icon: <OfficersIcon />,
    items: [
      'Full name, role, occupation',
      'Date of birth, nationality, country of residence',
      'Appointed on, resigned on, current status',
      'Other directorships held',
    ],
  },
  {
    num: '06',
    count: 12,
    title: 'Firmographic data',
    icon: <FirmographicIcon />,
    items: [
      'Phone, fax, domain, email',
      'Estimated employee numbers and revenue',
      'Business description and industry tag',
      'LinkedIn, Facebook and Instagram profiles',
      'Company logo',
    ],
  },
];

const TOTAL_FIELDS = CATEGORIES.reduce((sum, cat) => sum + cat.count, 0);

export const InvestmentScreeningCoverage = () => {
  const { t } = useInt();
  const headRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [counts, setCounts] = useState<number[]>(CATEGORIES.map(() => 0));

  useEffect(() => {
    const revealables = [headRef.current, ...cardRefs.current, noteRef.current].filter((el): el is HTMLDivElement =>
      Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setCounts(CATEGORIES.map((c) => c.count));
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.add(styles.revealed);
          observer.unobserve(entry.target);

          const idx = cardRefs.current.indexOf(entry.target as HTMLDivElement);

          if (idx !== -1 && !reduced) {
            const target = CATEGORIES[idx].count;
            const t0 = performance.now();
            const ms = 900;

            const frame = (now: number) => {
              const p = Math.min(1, (now - t0) / ms);
              const eased = 1 - (1 - p) ** 3;

              setCounts((prev) => {
                const next = [...prev];
                next[idx] = Math.round(target * eased);
                return next;
              });

              if (p < 1) {
                requestAnimationFrame(frame);
              }
            };

            requestAnimationFrame(frame);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="#0E1526" className={styles.root}>
      <div className={styles.glow} aria-hidden="true" />

      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          theme="dark"
          align="left"
          title={t('Every field on the record, sourced')}
          description={
            <>
              {TOTAL_FIELDS}{' '}
              {t(
                'fields across the six categories a deal team screens on, every one traced back to the registry it came from. Changes are recorded within 24 hours of the registry filing.',
              )}
            </>
          }
        />
      </div>

      <div className={styles.grid}>
        {CATEGORIES.map((cat, i) => (
          <div
            key={cat.num}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            className={cn(styles.card, styles.reveal)}
            style={{ transitionDelay: `${i * 90}ms` }}
          >
            <div className={styles.cardTop}>
              <span className={styles.num}>{cat.num}</span>
              <span className={styles.cnt}>
                <b>{counts[i]}</b> {t('fields')}
              </span>
            </div>
            <div className={styles.iconWrap}>{cat.icon}</div>
            <h3 className={styles.cardTitle}>{t(cat.title)}</h3>
            <ul className={styles.list}>
              {cat.items.map((item) => (
                <li key={item}>{t(item)}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div ref={noteRef} className={cn(styles.note, styles.reveal)}>
        <Link href="/data-dictionary" className={styles.pillBtn}>
          {t('View full data dictionary')}
          <ArrowIcon />
        </Link>
      </div>
    </Section>
  );
};
